import React, { useState, useCallback, useMemo } from 'react';

interface OrganizationOption {
  id: string;
  name: string;
  asanaProject?: string;
  portfolios?: {
    id: string;
    name: string;
    asanaProjectId: string;
  }[];
}

/** Flattened portfolio option for the dropdown */
interface PortfolioDropdownOption {
  /** Unique key used as the select value (asanaProjectId) */
  value: string;
  /** Display label: "OrgName → PortfolioName" or just org name for legacy */
  label: string;
}

interface EditableSummaryCardProps {
  finalJSON: any;
  organizationName: string;
  selectedOrgId: string;
  organizations?: OrganizationOption[];
  programOptions?: string[];
  onSubmit: (editedData: any, selectedOrgId: string) => void;
  onBack: () => void;
  disabled?: boolean;
}

/**
 * EditableSummaryCard — Phase 3: Final review before submission.
 * Shows all fields in an editable format so users can make last-minute changes.
 */
export const EditableSummaryCard: React.FC<EditableSummaryCardProps> = ({
  finalJSON,
  organizationName,
  selectedOrgId: initialOrgId,
  organizations = [],
  programOptions = [],
  onSubmit,
  onBack,
  disabled = false,
}) => {
  const [editedData, setEditedData] = useState({ ...finalJSON });
  const [editingField, setEditingField] = useState<string | null>(null);
  const [selectedOrg, setSelectedOrg] = useState(initialOrgId);

  /**
   * Build flattened portfolio options from organizations.
   * Shows "OrgName → PortfolioName" for orgs with portfolios,
   * or just "OrgName" for legacy orgs with asanaProject.
   */
  const portfolioOptions: PortfolioDropdownOption[] = useMemo(() => {
    const options: PortfolioDropdownOption[] = [];
    const multiOrg = organizations.length > 1;

    for (const org of organizations) {
      if (org.portfolios && org.portfolios.length > 0) {
        const showPrefix = multiOrg || org.portfolios.length > 1;
        for (const p of org.portfolios) {
          options.push({
            value: p.asanaProjectId,
            label: showPrefix ? `${org.name} → ${p.name}` : p.name,
          });
        }
      } else if (org.asanaProject) {
        // Legacy: org has asanaProject directly (no portfolios)
        options.push({
          value: org.asanaProject,
          label: org.name,
        });
      }
    }

    return options;
  }, [organizations]);

  const handleFieldChange = useCallback((field: string, value: string) => {
    setEditedData((prev: any) => ({ ...prev, [field]: value }));
  }, []);

  const handleSubmit = useCallback(() => {
    onSubmit(editedData, selectedOrg);
  }, [editedData, selectedOrg, onSubmit]);

  const fields = [
    // Fields from IRIS (editable)
    { key: 'title', label: 'Title', type: 'text' },
    { key: 'problem_statement', label: 'Problem Statement', type: 'textarea' },
    { key: 'desired_outcome', label: 'Desired Outcome', type: 'textarea' },
    { key: 'business_impact', label: 'Business Impact', type: 'textarea' },
    { key: 'stakeholders', label: 'Stakeholders', type: 'text' },
    { key: 'program_name', label: 'Program', type: programOptions.length > 0 ? 'select' : 'text' },
    // Fields from Structured Capture (editable for last-minute changes)
    { key: 'request_type', label: 'Request Type', type: 'text' },
    { key: 'target_year', label: 'Target Year', type: 'text' },
    { key: 'annualized_entitlement', label: 'Annualized Entitlement', type: 'number', helper: 'Enter full number (e.g., 40000000 not 40M)' },
    { key: 'impact_metrics', label: 'Impact Metric', type: 'text' },
  ];

  // Determine the display name for the selected portfolio
  const selectedDisplayName = useMemo(() => {
    const match = portfolioOptions.find(o => o.value === selectedOrg);
    return match?.label || organizationName;
  }, [portfolioOptions, selectedOrg, organizationName]);

  return (
    <div className="es-card">
      <div className="es-header">
        <div className="sc-avatar">🐴</div>
        <div>
          <h3 className="es-title">📋 Review Your Request</h3>
          <p className="es-subtitle">Click any field to edit before submitting.</p>
        </div>
      </div>

      <div className="es-fields">
        {fields.map(field => {
          const value = editedData[field.key] || '';
          if (!value && field.type === 'readonly') return null;
          const isEditing = editingField === field.key;

          return (
            <div key={field.key} className="es-field">
              <div className="es-field-label">{field.label}</div>
              {field.type === 'readonly' ? (
                <div className="es-field-value es-readonly">{value}</div>
              ) : field.type === 'select' ? (
                <select
                  className="es-field-input"
                  value={value}
                  onChange={e => handleFieldChange(field.key, e.target.value)}
                  disabled={disabled}
                >
                  <option value="">Select...</option>
                  {programOptions.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              ) : isEditing ? (
                field.type === 'textarea' ? (
                  <textarea
                    className="es-field-input es-textarea"
                    value={value}
                    onChange={e => handleFieldChange(field.key, e.target.value)}
                    onBlur={() => setEditingField(null)}
                    autoFocus
                    rows={3}
                    disabled={disabled}
                  />
                ) : (
                  <input
                    className="es-field-input"
                    type="text"
                    value={value}
                    onChange={e => handleFieldChange(field.key, e.target.value)}
                    onBlur={() => setEditingField(null)}
                    onKeyDown={e => e.key === 'Enter' && setEditingField(null)}
                    autoFocus
                    disabled={disabled}
                  />
                )
              ) : (
                <div
                  className={`es-field-value${!value ? ' es-empty' : ''}`}
                  onClick={() => field.type !== 'readonly' && !disabled && setEditingField(field.key)}
                  title="Click to edit"
                >
                  {value || '—'}
                  {field.type !== 'readonly' && <span className="es-edit-icon">✏️</span>}
                </div>
              )}
              {(field as any).helper && isEditing && (
                <p style={{ fontSize: '11px', color: '#718096', margin: '4px 0 0 0', fontStyle: 'italic' }}>
                  💡 {(field as any).helper}
                </p>
              )}
            </div>
          );
        })}

        {/* Portfolio selection */}
        <div className="es-field">
          <div className="es-field-label">Portfolio</div>
          {portfolioOptions.length > 1 ? (
            <select
              className="es-field-input"
              value={selectedOrg}
              onChange={e => setSelectedOrg(e.target.value)}
              disabled={disabled}
            >
              {portfolioOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          ) : (
            <div className="es-field-value es-readonly">{selectedDisplayName}</div>
          )}
        </div>
      </div>

      <div className="es-actions">
        <button
          type="button"
          className="es-submit-btn"
          onClick={handleSubmit}
          disabled={disabled}
        >
          ✅ Submit to Asana
        </button>
        <button
          type="button"
          className="es-back-btn"
          onClick={onBack}
          disabled={disabled}
        >
          ← Back
        </button>
      </div>
    </div>
  );
};

export default EditableSummaryCard;